import { FFmpeg } from '@ffmpeg/ffmpeg'
import { fetchFile, toBlobURL } from '@ffmpeg/util'
import { blockUI, unblockUI } from './blockUI'

const CORE_VERSION = '0.12.6'
const TARGET_MAX_BYTES = 7 * 1024 * 1024
const isVideoCandidate = (file: File): boolean =>
  file.type?.startsWith('video/')

type EncodeProfile = {
  width: number
  height: number
  crf: number
  maxrate: string
  bufsize: string
  audioBitrate: string
}

const ENCODE_PROFILES: EncodeProfile[] = [
  { width: 1280, height: 720, crf: 30, maxrate: '2000k', bufsize: '4000k', audioBitrate: '96k' },
  { width: 960, height: 540, crf: 32, maxrate: '1200k', bufsize: '2400k', audioBitrate: '80k' },
  { width: 854, height: 480, crf: 34, maxrate: '800k', bufsize: '1600k', audioBitrate: '80k' },
]

type LifecycleHook = {
  onStart?: () => void
  onEnd?: () => void
}

const baseURL = `https://unpkg.com/@ffmpeg/core@${CORE_VERSION}/dist/umd`

let ffmpegInstance: FFmpeg | null = null
let ffmpegLoadPromise: Promise<void> | null = null

const getFfmpeg = async (): Promise<FFmpeg> => {
  if (!ffmpegInstance) {
    ffmpegInstance = new FFmpeg()
  }

  if (!ffmpegLoadPromise) {
    ffmpegLoadPromise = (async () => {
      const [coreURL, wasmURL] = await Promise.all([
        toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      ])

      await ffmpegInstance!.load({
        coreURL,
        wasmURL,
      })
    })().catch((error) => {
      ffmpegLoadPromise = null
      throw error
    })
  }

  await ffmpegLoadPromise
  return ffmpegInstance
}

const buildFilter = (width: number, height: number): string =>
  `scale=${width}:${height}:force_original_aspect_ratio=increase,crop=${width}:${height},setsar=1`

const encodeWithProfile = async (
  ffmpeg: FFmpeg,
  inputName: string,
  outputName: string,
  profile: EncodeProfile,
) => {
  const filter = buildFilter(profile.width, profile.height)

  await ffmpeg.exec([
    '-y',
    '-i',
    inputName,
    '-vf',
    filter,
    '-c:v',
    'libx264',
    '-preset',
    'veryfast',
    '-crf',
    String(profile.crf),
    '-maxrate',
    profile.maxrate,
    '-bufsize',
    profile.bufsize,
    '-c:a',
    'aac',
    '-b:a',
    profile.audioBitrate,
    '-movflags',
    'faststart',
    outputName,
  ])
}

const encodeVideoFile = async (file: File): Promise<File> => {
  const ffmpeg = await getFfmpeg()
  const timeSuffix = Date.now()
  const inputName = `input_${timeSuffix}${file.name.endsWith('.mp4') ? '.mp4' : ''}`
  const fileData = await fetchFile(file)

  await ffmpeg.writeFile(inputName, fileData)

  let finalData: Uint8Array | null = null

  for (let index = 0; index < ENCODE_PROFILES.length; index += 1) {
    const profile = ENCODE_PROFILES[index]
    const outputName = `output_${timeSuffix}_${index}.mp4`

    await encodeWithProfile(ffmpeg, inputName, outputName, profile)

    const outputData = await ffmpeg.readFile(outputName)
    await ffmpeg.deleteFile(outputName).catch(() => {})

    const binaryData =
      typeof outputData === 'string'
        ? new TextEncoder().encode(outputData)
        : outputData

    finalData = binaryData
    const withinLimit = binaryData.length <= TARGET_MAX_BYTES

    if (withinLimit || index === ENCODE_PROFILES.length - 1) {
      break
    }
  }

  try {
    await ffmpeg.deleteFile(inputName)
  } catch {
    // ignore
  }

  if (!finalData) {
    throw new Error('비디오 인코딩에 실패했습니다.')
  }

  const optimizedBlob = new Blob([finalData], { type: 'video/mp4' })
  const sanitizedName = file.name.replace(/\.[^/.]+$/, '')

  return new File([optimizedBlob], `${sanitizedName}_encoded.mp4`, {
    type: 'video/mp4',
  })
}

export const optimizeFormDataVideos = async (
  data: FormData,
  lifecycle?: LifecycleHook,
): Promise<{ formData: FormData; converted: boolean; hasVideo: boolean }> => {
  const entries = Array.from(data.entries())
  const hasVideo = entries.some(
    ([, value]) => value instanceof File && isVideoCandidate(value),
  )

  if (!hasVideo) {
    return { formData: data, converted: false, hasVideo: false }
  }

  const consent =
    typeof window === 'undefined'
      ? true
      : window.confirm('동영상 최적화를 진행하시겠습니까?\n확인을 누르면 최적화 후 업로드되고, 취소하면 원본 그대로 업로드됩니다.')

  if (!consent) {
    return { formData: data, converted: false, hasVideo: true }
  }

  blockUI()
  lifecycle?.onStart?.()

  const optimized = new FormData()

  try {
    for (const [key, value] of entries) {
      if (value instanceof File && isVideoCandidate(value)) {
        const convertedFile = await encodeVideoFile(value)
        optimized.append(key, convertedFile, convertedFile.name)
      } else if (value instanceof Blob) {
        const blobName = value instanceof File ? value.name : undefined
        optimized.append(key, value, blobName)
      } else {
        optimized.append(key, value as string)
      }
    }

    return { formData: optimized, converted: true, hasVideo: true }
  } finally {
    lifecycle?.onEnd?.()
    unblockUI()
  }
}
